import { getAIProviderManager } from "./aiProviderFactory";
import {
  extractResourcesForCourses,
  selectRelevantResources,
  formatResourcesForPrompt,
  selectWarmUpFromResources,
  formatWarmupsForPrompt,
} from "./courseResourceExtractor";
import {
  buildLessonPlanComparePrompt,
  determineLessonPlanChallengeStrategy,
  evaluateLessonPlanCandidate,
  parseClassPeriodMinutes,
  selectDeterministicLessonPlanWinner,
  selectLessonPlanWinner,
} from "./lessonPlanCandidateSelector";
import {
  extractKeywords,
  calculateWeightedRelevance,
} from "./keywordExtractor";
import { LLM_SENSITIVITY } from "./llmRoutingPolicy";

const RESOURCE_CACHE_TTL_MS = 12 * 60 * 1000;
const RESOURCE_CACHE_MAX_ENTRIES = 8;
const RESOURCE_CONTEXT_CACHE = new Map();
const CONTEXT_STATE_VERSION = "2026-03-18";

const normalizeObject = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeObject(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeObject(value[key]);
        return accumulator;
      }, {});
  }
  return value;
};

const stableStringify = (value) => JSON.stringify(normalizeObject(value));

const hashString = (value) => {
  const text = String(value || "");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const trimText = (value, limit = 240) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};

const toStringList = (value, limit = 6) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, limit);
};

const summarizeFiles = (files = []) => ({
  count: Array.isArray(files) ? files.length : 0,
  names: Array.isArray(files)
    ? files
        .map((file) => String(file?.name || "").trim())
        .filter(Boolean)
        .slice(0, 3)
    : [],
});

const summarizeInsightContext = (context = {}) => {
  if (!context || typeof context !== "object") return null;
  const summary = {
    source: context.source || null,
    focus: context.focus || null,
    className: context.className || null,
    gradeLevels: toStringList(context.gradeLevels || [], 4),
    standards: toStringList(context.standards || [], 4),
    assignmentTrendSummary:
      trimText(context.assignmentTrendSummary, 200) || null,
  };
  return Object.values(summary).some(Boolean) ? summary : null;
};

const summarizeUploadAnalysis = (analysis = {}) => {
  if (!analysis || typeof analysis !== "object") return null;
  const summary = {
    analyzedFiles: Number(analysis.analyzedFiles || 0),
    totalFiles: Number(analysis.totalFiles || 0),
    keyConcepts: toStringList(analysis.keyConcepts || [], 6),
    instructionalSignals: toStringList(analysis.instructionalSignals || [], 6),
    standardsMentions: toStringList(analysis.standardsMentions || [], 6),
  };
  const hasData = summary.analyzedFiles > 0 || summary.totalFiles > 0;
  return hasData ? summary : null;
};

const buildCourseSignature = (course) => ({
  id: course?.id || null,
  name: course?.name || null,
  curriculum: course?.curriculum || null,
  warmups: course?.warmups || null,
  extra: course?.extra || null,
  youtube: course?.youtube || null,
  extrayoutube: course?.extrayoutube || null,
  extrayoutube1: course?.extrayoutube1 || null,
});

const buildResourceCacheKey = ({
  userInput,
  courses,
  generationType,
  options,
}) => {
  const payload = {
    userInput: String(userInput || "").trim(),
    generationType: String(generationType || "lessonPlan"),
    options: {
      preferredCourseId: options?.preferredCourseId || "",
      preferredCourseName: options?.preferredCourseName || "",
    },
    courses: Array.isArray(courses) ? courses.map(buildCourseSignature) : [],
  };
  return hashString(stableStringify(payload));
};

const readResourceCache = (cacheKey) => {
  if (!cacheKey) return null;
  const entry = RESOURCE_CONTEXT_CACHE.get(cacheKey);
  if (!entry) return null;
  if (Date.now() - entry.createdAt > RESOURCE_CACHE_TTL_MS) {
    RESOURCE_CONTEXT_CACHE.delete(cacheKey);
    return null;
  }
  return entry.value;
};

const writeResourceCache = (cacheKey, value) => {
  if (!cacheKey) return;
  RESOURCE_CONTEXT_CACHE.set(cacheKey, {
    value,
    createdAt: Date.now(),
  });
  if (RESOURCE_CONTEXT_CACHE.size <= RESOURCE_CACHE_MAX_ENTRIES) return;
  const oldestKey = RESOURCE_CONTEXT_CACHE.keys().next().value;
  if (oldestKey) {
    RESOURCE_CONTEXT_CACHE.delete(oldestKey);
  }
};

/**
 * Calculate relevance score for a course based on user input
 * Uses advanced keyword extraction with proper noun detection and phrase matching
 */
function calculateRelevance(course, userInput, keywords) {
  const courseName = (course.name || "").toLowerCase();
  const courseIntro = (course.intro || "").toLowerCase();
  const courseDesc =
    typeof course.description === "string"
      ? course.description.toLowerCase()
      : "";

  let score = 0;

  // Use weighted keyword matching for course name (highest priority)
  score += calculateWeightedRelevance(keywords, courseName) * 2; // 2x multiplier for name matches

  // Use weighted keyword matching for intro
  score += calculateWeightedRelevance(keywords, courseIntro);

  // Use weighted keyword matching for description
  score += calculateWeightedRelevance(keywords, courseDesc) * 0.5; // 0.5x multiplier (lower priority)

  // Bonus for courses with YouTube playlists
  if (course.youtube || course.extrayoutube || course.extrayoutube1) {
    score += 2;
  }

  return score;
}

function buildCourseRelevance(userInput, courses = []) {
  const keywords = extractKeywords(userInput);
  const validCourses =
    courses?.filter((c) => c?.name && (c?.intro || c?.description)) || [];

  const rankedCourses = validCourses
    .map((course) => ({
      ...course,
      relevanceScore: calculateRelevance(course, userInput, keywords),
    }))
    .sort((a, b) => b.relevanceScore - a.relevanceScore);

  const highlyRelevant = rankedCourses
    .filter((c) => c.relevanceScore > 10)
    .slice(0, 5);
  const somewhatRelevant = rankedCourses
    .filter((c) => c.relevanceScore > 5 && c.relevanceScore <= 10)
    .slice(0, 5);
  const topCourses = rankedCourses.slice(0, 5);

  return {
    keywords,
    rankedCourses,
    highlyRelevant,
    somewhatRelevant,
    topCourses,
  };
}

function buildCoursesSection(highlyRelevant = [], somewhatRelevant = []) {
  let courseList = "";

  if (highlyRelevant.length > 0) {
    courseList += "**HIGHLY RELEVANT Courses (prioritize these):**\n";
    courseList += highlyRelevant
      .map((course) => {
        const desc = course.intro || course.description;
        const shortDesc =
          typeof desc === "string" ? desc.substring(0, 150) : desc;
        let entry = `- **${course.name}**: ${shortDesc}`;

        const playlists = [];
        if (course.youtube) playlists.push(course.youtube);
        if (course.extrayoutube) playlists.push(course.extrayoutube);
        if (course.extrayoutube1) playlists.push(course.extrayoutube1);

        if (playlists.length > 0) {
          entry += `\n  YouTube Playlists: ${playlists.join(", ")}`;
        }

        return entry;
      })
      .join("\n");
  }

  if (somewhatRelevant.length > 0) {
    if (highlyRelevant.length > 0) courseList += "\n\n";
    courseList += "**Other Potentially Relevant Courses:**\n";
    courseList += somewhatRelevant
      .map((course) => {
        const desc = course.intro || course.description;
        const shortDesc =
          typeof desc === "string" ? desc.substring(0, 100) : desc;
        let entry = `- **${course.name}**: ${shortDesc}`;

        const playlists = [];
        if (course.youtube) playlists.push(course.youtube);
        if (course.extrayoutube) playlists.push(course.extrayoutube);
        if (course.extrayoutube1) playlists.push(course.extrayoutube1);

        if (playlists.length > 0) {
          entry += `\n  YouTube Playlists: ${playlists.join(", ")}`;
        }

        return entry;
      })
      .join("\n");
  }

  return courseList
    ? `\n\nAvailable Courses with Video Resources:\n${courseList}\n\n**IMPORTANT**: Prioritize the HIGHLY RELEVANT courses listed above. Only include YouTube playlists from courses that are truly relevant to the lesson topic.`
    : "";
}

const normalizeCourseKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const resolvePreferredCourse = (courses = [], options = {}) => {
  if (!courses || courses.length === 0) return null;
  const preferredCourseId = options?.preferredCourseId
    ? String(options.preferredCourseId).trim()
    : "";
  const preferredCourseName = normalizeCourseKey(options?.preferredCourseName);

  if (preferredCourseId) {
    const matchById = courses.find((course) => {
      if (course?.id === undefined || course?.id === null) return false;
      return String(course.id).trim() === preferredCourseId;
    });
    if (matchById) return matchById;
  }

  if (preferredCourseName) {
    const matchByName = courses.find(
      (course) => normalizeCourseKey(course?.name) === preferredCourseName,
    );
    if (matchByName) return matchByName;
  }

  return null;
};

const buildResourceOptions = (criteria = {}) => {
  const preferredCourseId =
    criteria?.preferredCourseId ||
    criteria?.linkedCourseId ||
    criteria?.courseId ||
    "";
  const preferredCourseName =
    criteria?.preferredCourseName ||
    criteria?.linkedCourseName ||
    criteria?.courseName ||
    criteria?.curriculumContext?.courseTitle ||
    criteria?.className ||
    "";

  const options = {
    preferredCourseId: preferredCourseId
      ? String(preferredCourseId).trim()
      : "",
    preferredCourseName: preferredCourseName
      ? String(preferredCourseName).trim()
      : "",
  };

  if (!options.preferredCourseId && !options.preferredCourseName) {
    return {};
  }

  return options;
};

export async function prepareLessonPlanResources(
  userInput,
  courses = [],
  generationType = "lessonPlan",
  options = {},
) {
  const cacheKey = buildResourceCacheKey({
    userInput,
    courses,
    generationType,
    options,
  });
  const cached = readResourceCache(cacheKey);
  if (cached) {
    return {
      ...cached,
      cache: {
        hit: true,
        key: cacheKey,
      },
    };
  }

  const {
    keywords,
    rankedCourses,
    highlyRelevant,
    somewhatRelevant,
    topCourses,
  } = buildCourseRelevance(userInput, courses);

  console.log(
    "Extracted keywords:",
    keywords.map((k) => `${k.text} (${k.weight}x, ${k.type})`),
  );
  console.log(
    "Course relevance scores:",
    rankedCourses
      .slice(0, 10)
      .map((c) => ({ name: c.name, score: c.relevanceScore })),
  );

  let extractedResourcesSection = "";
  let warmupQuestionsSection = "";
  let selectedWarmups = null;
  let relevantResources = [];
  const preferredCourse = resolvePreferredCourse(courses, options);
  const extractionCourses = preferredCourse
    ? [
        preferredCourse,
        ...topCourses.filter((course) => {
          if (!course) return false;
          if (
            preferredCourse?.id !== undefined &&
            preferredCourse?.id !== null
          ) {
            return String(course.id) !== String(preferredCourse.id);
          }
          return (
            normalizeCourseKey(course?.name) !==
            normalizeCourseKey(preferredCourse?.name)
          );
        }),
      ].slice(0, 5)
    : topCourses;

  try {
    console.log("Extracting detailed resources from top courses...");
    const extractedData = await extractResourcesForCourses(
      extractionCourses,
      userInput,
    );

    if (extractedData && extractedData.length > 0) {
      relevantResources = selectRelevantResources(
        extractedData,
        userInput,
        25,
        keywords,
        options,
      );
      if (relevantResources.length > 0) {
        extractedResourcesSection = formatResourcesForPrompt(relevantResources);
        console.log(
          `✓ Found ${relevantResources.length} relevant resources from course docs and videos`,
        );
      }

      console.log("Looking for warm-up questions in extracted data...");
      selectedWarmups = selectWarmUpFromResources(
        extractedData,
        userInput,
        options,
      );
      if (selectedWarmups) {
        warmupQuestionsSection = formatWarmupsForPrompt(selectedWarmups);
        console.log(
          `✓ Found ${selectedWarmups.warmups.length} ${selectedWarmups.source} warm-up questions`,
        );
      } else if (
        generationType === "warmUp" ||
        generationType === "lessonPlan"
      ) {
        console.log("No warm-up questions were selected");
      }
    }
  } catch (error) {
    console.error("Error extracting course resources:", error);
  }

  const curriculumUnitTitles = Array.from(
    new Set(
      relevantResources
        .filter((item) => item.docType === "curriculum" && item.text)
        .map((item) => item.text.trim())
        .filter((text) => text.length > 0),
    ),
  );

  const coursesSection = buildCoursesSection(highlyRelevant, somewhatRelevant);

  const resourceContext = {
    keywords,
    rankedCourses,
    highlyRelevant,
    somewhatRelevant,
    topCourses: extractionCourses,
    relevantResources,
    selectedWarmups,
    extractedResourcesSection,
    warmupQuestionsSection,
    coursesSection,
    curriculumUnitTitles,
  };

  writeResourceCache(cacheKey, resourceContext);

  return {
    ...resourceContext,
    cache: {
      hit: false,
      key: cacheKey,
    },
  };
}

function formatCurriculumContext(context = {}) {
  if (!context || typeof context !== "object") return "";
  const parts = [];
  if (context.courseTitle) parts.push(`Course: ${context.courseTitle}`);
  if (context.unitTitle) parts.push(`Unit: ${context.unitTitle}`);
  if (context.lessonTitle) parts.push(`Lesson: ${context.lessonTitle}`);
  if (context.activityTitle) parts.push(`Activity: ${context.activityTitle}`);
  if (context.miniLessonTitle)
    parts.push(`Mini-lesson: ${context.miniLessonTitle}`);
  return parts.join(" | ");
}

function buildUserChoicesContext(criteria = {}, metadata = {}) {
  const lines = [];

  if (criteria.grades && criteria.grades.length > 0) {
    lines.push(`- Grade levels: ${criteria.grades.join(", ")}`);
  }

  if (criteria.standards && criteria.standards.length > 0) {
    lines.push(`- Standards: ${criteria.standards.join(", ")}`);
  }

  if (criteria.district && String(criteria.district).trim()) {
    lines.push(`- School district: ${criteria.district}`);
  }

  if (criteria.classPeriodLength && String(criteria.classPeriodLength).trim()) {
    const duration =
      criteria.classPeriodLength === "custom"
        ? "custom duration"
        : `${criteria.classPeriodLength} minutes`;
    lines.push(`- Class period length: ${duration}`);
  }

  if (criteria.udlStrategies && criteria.udlStrategies.length > 0) {
    lines.push(`- UDL strategies selected: ${criteria.udlStrategies.length}`);
  }

  if (criteria.additional && String(criteria.additional).trim()) {
    lines.push(`- Additional requirements provided`);
  }

  if (criteria.driveFiles && criteria.driveFiles.length > 0) {
    lines.push(
      `- Drive materials provided: ${criteria.driveFiles.length} file(s)`,
    );
  }

  if (criteria.localFiles && criteria.localFiles.length > 0) {
    lines.push(
      `- Uploaded materials provided: ${criteria.localFiles.length} file(s)`,
    );
  }

  if (criteria.oakLessons && criteria.oakLessons.length > 0) {
    lines.push(
      `- Oak Curriculum lessons included: ${criteria.oakLessons.length}`,
    );
  }

  if (criteria.curriculumContext) {
    const contextLine = formatCurriculumContext(criteria.curriculumContext);
    if (contextLine) {
      lines.push(`- Curriculum context: ${contextLine}`);
    }
  }

  if (criteria.backwardDesign) {
    lines.push("- Backward design summary provided");
  }

  if (criteria.essentialQuestions && criteria.essentialQuestions.length > 0) {
    lines.push(
      `- Essential questions provided: ${criteria.essentialQuestions.length}`,
    );
  }

  if (
    criteria.enduringUnderstandings &&
    criteria.enduringUnderstandings.length > 0
  ) {
    lines.push(
      `- Enduring understandings provided: ${criteria.enduringUnderstandings.length}`,
    );
  }

  if (
    criteria.lessonBlueprint &&
    typeof criteria.lessonBlueprint === "object"
  ) {
    lines.push("- Lesson blueprint provided");
  }

  if (criteria.includeEssentials === false) {
    lines.push(
      "- Essential questions and enduring understandings excluded from output",
    );
  }

  if (criteria.warmUpQuestion && String(criteria.warmUpQuestion).trim()) {
    lines.push("- Warm-up question provided");
  }

  if (criteria.includeWarmUp === false) {
    lines.push("- Warm-up question excluded from the lesson plan");
  }

  if (criteria.previousLessonPlanProvided) {
    lines.push("- Previous lesson plan provided");
  }

  if (criteria.useAssignmentData) {
    lines.push("- Using assignment analytics for follow-up");
  }

  if (criteria.coherenceGenerationMode === "adjustNonEdited") {
    lines.push(
      "- Generation mode: adjust non-edited content around teacher changes",
    );
  } else if (criteria.coherenceGenerationMode === "currentContext") {
    lines.push(
      "- Generation mode: generate from the current context as written",
    );
  }

  if (criteria.teacherAnchors && typeof criteria.teacherAnchors === "object") {
    lines.push("- Teacher anchor context included");
  }

  if (criteria.insightContext && typeof criteria.insightContext === "object") {
    lines.push("- Gradebook/class insight context included");
  }

  if (
    criteria.uploadedMaterialsAnalysis &&
    typeof criteria.uploadedMaterialsAnalysis === "object"
  ) {
    const analyzedCount = Number(
      criteria.uploadedMaterialsAnalysis.analyzedFiles || 0,
    );
    if (analyzedCount > 0) {
      lines.push(
        `- Uploaded material analysis included: ${analyzedCount} file(s)`,
      );
    }
  }

  if (metadata && typeof metadata.analyticsConsent === "boolean") {
    lines.push(
      `- Analytics consent: ${metadata.analyticsConsent ? "yes" : "no"}`,
    );
  }

  if (lines.length === 0) {
    return "";
  }

  return `\n\nUSER CHOICES CONTEXT:\n${lines.join("\n")}\n`;
}

function buildNestedPromptContext(criteria = {}) {
  const lines = [];

  if (criteria.essentialQuestions && criteria.essentialQuestions.length > 0) {
    lines.push(
      `- Essential Questions: ${criteria.essentialQuestions.join(" | ")}`,
    );
  }

  if (
    criteria.enduringUnderstandings &&
    criteria.enduringUnderstandings.length > 0
  ) {
    lines.push(
      `- Enduring Understandings: ${criteria.enduringUnderstandings.join(" | ")}`,
    );
  }

  if (criteria.warmUpQuestion && String(criteria.warmUpQuestion).trim()) {
    lines.push(`- Warm-Up Question: ${criteria.warmUpQuestion.trim()}`);
  }

  if (criteria.previousLessonPlanProvided) {
    lines.push("- Previous lesson plan provided (build on it).");
  }

  if (criteria.useAssignmentData) {
    lines.push(
      "- Assignment analytics provided (address gaps and misconceptions).",
    );
  }

  if (lines.length === 0) return "";

  return (
    `\n\nNESTED CONTEXT CHAIN:\n${lines.join("\n")}\n` +
    "Use this chain to keep the plan coherent: objectives align to EQ/EU; " +
    "warm-up primes those objectives; assessment checks the same targets; " +
    "analytics-informed adjustments address student gaps.\n"
  );
}

function formatInsightContextSection(criteria = {}) {
  const context =
    criteria && typeof criteria.insightContext === "object"
      ? criteria.insightContext
      : null;
  if (!context) return "";

  const lines = [];
  if (context.source) lines.push(`- Insight source: ${context.source}`);
  if (context.focus) lines.push(`- Priority focus: ${context.focus}`);
  if (context.className) lines.push(`- Class: ${context.className}`);
  if (context.linkedCourseName) {
    lines.push(`- Linked course: ${context.linkedCourseName}`);
  }
  if (Array.isArray(context.gradeLevels) && context.gradeLevels.length > 0) {
    lines.push(`- Grade levels: ${context.gradeLevels.join(", ")}`);
  }
  if (Array.isArray(context.standards) && context.standards.length > 0) {
    lines.push(`- Standards: ${context.standards.join(", ")}`);
  }
  if (
    Array.isArray(context.essentialQuestions) &&
    context.essentialQuestions.length > 0
  ) {
    lines.push(
      `- Essential questions: ${context.essentialQuestions.join(" | ")}`,
    );
  }
  if (
    Array.isArray(context.enduringUnderstandings) &&
    context.enduringUnderstandings.length > 0
  ) {
    lines.push(
      `- Enduring understandings: ${context.enduringUnderstandings.join(" | ")}`,
    );
  }
  if (context.assignmentTrendSummary) {
    lines.push(
      `- Assignment trends summary: ${context.assignmentTrendSummary}`,
    );
  }
  if (
    Array.isArray(context.assignmentTrendSections) &&
    context.assignmentTrendSections.length > 0
  ) {
    lines.push(
      `- Assignment trend details: ${context.assignmentTrendSections.join(" | ")}`,
    );
  }
  if (context.importedTopicSuggestion) {
    lines.push(`- Suggested topic anchor: ${context.importedTopicSuggestion}`);
  }
  if (context.importedAdditionalCriteria) {
    lines.push(
      `- Imported planning notes: ${context.importedAdditionalCriteria}`,
    );
  }
  if (context.latestKnowledgeLoopTopic) {
    lines.push(
      `- Latest planned target topic: ${context.latestKnowledgeLoopTopic}`,
    );
  }
  if (context.latestKnowledgeLoopObjective) {
    lines.push(
      `- Latest planned objective snapshot: ${context.latestKnowledgeLoopObjective}`,
    );
  }
  if (context.latestKnowledgeLoopAssessment) {
    lines.push(
      `- Latest planned assessment snapshot: ${context.latestKnowledgeLoopAssessment}`,
    );
  }

  if (lines.length === 0) return "";
  return `\n\n**GRADEBOOK & CLASS INSIGHT CONTEXT:**\n${lines.join("\n")}\n`;
}

function formatUploadedMaterialAnalysisSection(criteria = {}) {
  const analysis =
    criteria && typeof criteria.uploadedMaterialsAnalysis === "object"
      ? criteria.uploadedMaterialsAnalysis
      : null;
  if (!analysis) return "";

  const lines = [];
  const analyzedFiles = Number(analysis.analyzedFiles || 0);
  const totalFiles = Number(analysis.totalFiles || 0);
  if (analyzedFiles > 0) {
    lines.push(
      `- Files analyzed: ${analyzedFiles}/${totalFiles || analyzedFiles}`,
    );
  }
  if (Array.isArray(analysis.keyConcepts) && analysis.keyConcepts.length > 0) {
    lines.push(`- Recurring concepts: ${analysis.keyConcepts.join(", ")}`);
  }
  if (
    Array.isArray(analysis.instructionalSignals) &&
    analysis.instructionalSignals.length > 0
  ) {
    lines.push(
      `- Instructional signals: ${analysis.instructionalSignals.join(", ")}`,
    );
  }
  if (
    Array.isArray(analysis.standardsMentions) &&
    analysis.standardsMentions.length > 0
  ) {
    lines.push(
      `- Standards mentioned in uploads: ${analysis.standardsMentions.join(", ")}`,
    );
  }
  if (Array.isArray(analysis.highlights) && analysis.highlights.length > 0) {
    lines.push("- Key upload excerpts:");
    analysis.highlights.slice(0, 4).forEach((item) => {
      const fileName = String(item?.fileName || "").trim();
      const excerpt = String(item?.excerpt || "").trim();
      if (fileName && excerpt) {
        lines.push(`  - ${fileName}: ${excerpt}`);
      }
    });
  }

  if (lines.length === 0) return "";
  return `\n\n**ANALYSIS OF UPLOADED MATERIALS:**\n${lines.join("\n")}\n`;
}

function formatLessonBlueprintSection(criteria = {}, options = {}) {
  const blueprint =
    criteria && typeof criteria.lessonBlueprint === "object"
      ? criteria.lessonBlueprint
      : null;
  if (!blueprint) return "";

  const objective = String(blueprint.objective || "").trim();
  const assessment = String(blueprint.assessment || "").trim();
  const keyPoints = Array.isArray(blueprint.keyPoints)
    ? blueprint.keyPoints
    : [];
  const lessonArc = Array.isArray(blueprint.lessonArc)
    ? blueprint.lessonArc
    : [];
  const lines = [];

  if (objective) lines.push(`- Objective: ${objective}`);
  if (keyPoints.length > 0)
    lines.push(`- Key points: ${keyPoints.join(" | ")}`);
  if (assessment) lines.push(`- Assessment: ${assessment}`);
  if (lessonArc.length > 0)
    lines.push(`- Lesson arc: ${lessonArc.join(" | ")}`);

  if (lines.length === 0) return "";

  const isTeacherApproved = Boolean(criteria?.teacherEditedState?.blueprint);
  const isChallenger = options?.variant === "challenger";

  if (isTeacherApproved) {
    return (
      `\n\n**LESSON BLUEPRINT (TEACHER-APPROVED):**\n${lines.join("\n")}\n` +
      "Use this blueprint as the authoritative structure for the lesson plan. Do not change the objective or assessment unless explicitly asked.\n"
    );
  }

  if (isChallenger) {
    return (
      `\n\n**CURRENT LESSON BLUEPRINT DRAFT (SOFT GUIDE):**\n${lines.join("\n")}\n` +
      "Use this draft as optional guidance only. If it feels repetitive, overloaded, or too vague, improve it while keeping the same lesson goal.\n"
    );
  }

  return (
    `\n\n**CURRENT LESSON BLUEPRINT DRAFT:**\n${lines.join("\n")}\n` +
    "Use this draft as a guide, but tighten the scope, sharpen the objective, and improve flow if needed.\n"
  );
}

function formatTeacherAnchorSection(criteria = {}) {
  if (criteria.coherenceGenerationMode !== "adjustNonEdited") {
    return "";
  }

  const anchors =
    criteria && typeof criteria.teacherAnchors === "object"
      ? criteria.teacherAnchors
      : null;
  if (!anchors) return "";

  const lines = [];

  if (anchors.additionalCriteria) {
    lines.push(
      `- Additional criteria to preserve: ${anchors.additionalCriteria}`,
    );
  }
  if (anchors.warmUpQuestion) {
    lines.push(`- Warm-up question to preserve: ${anchors.warmUpQuestion}`);
  }
  if (
    Array.isArray(anchors.essentialQuestions) &&
    anchors.essentialQuestions.length > 0
  ) {
    lines.push(
      `- Essential questions to preserve: ${anchors.essentialQuestions.join(" | ")}`,
    );
  }
  if (
    Array.isArray(anchors.enduringUnderstandings) &&
    anchors.enduringUnderstandings.length > 0
  ) {
    lines.push(
      `- Enduring understandings to preserve: ${anchors.enduringUnderstandings.join(" | ")}`,
    );
  }
  if (anchors.lessonBlueprint && typeof anchors.lessonBlueprint === "object") {
    const blueprintLines = [];
    if (anchors.lessonBlueprint.objective) {
      blueprintLines.push(`Objective: ${anchors.lessonBlueprint.objective}`);
    }
    if (
      Array.isArray(anchors.lessonBlueprint.keyPoints) &&
      anchors.lessonBlueprint.keyPoints.length > 0
    ) {
      blueprintLines.push(
        `Key points: ${anchors.lessonBlueprint.keyPoints.join(" | ")}`,
      );
    }
    if (anchors.lessonBlueprint.assessment) {
      blueprintLines.push(`Assessment: ${anchors.lessonBlueprint.assessment}`);
    }
    if (
      Array.isArray(anchors.lessonBlueprint.lessonArc) &&
      anchors.lessonBlueprint.lessonArc.length > 0
    ) {
      blueprintLines.push(
        `Lesson arc: ${anchors.lessonBlueprint.lessonArc.join(" | ")}`,
      );
    }
    blueprintLines.forEach((line) => lines.push(`- Blueprint anchor: ${line}`));
  }

  if (lines.length === 0) return "";

  return `\n\n**TEACHER-SHAPED ANCHORS TO PRESERVE:**\n${lines.join("\n")}\n`;
}

function buildGenerationModePrompt(
  criteria = {},
  generationType = "lessonPlan",
) {
  const mode =
    criteria.coherenceGenerationMode === "currentContext"
      ? "currentContext"
      : "adjustNonEdited";
  const artifactLabel =
    generationType === "lessonBlueprint"
      ? "lesson blueprint"
      : generationType === "lessonPlan"
        ? "lesson plan"
        : "output";

  if (mode === "adjustNonEdited") {
    return (
      `\n\nCOHERENCE MODE: Adjust non-edited content to vibe with teacher changes.\n` +
      `- Treat the teacher-shaped anchors below as authoritative when writing the ${artifactLabel}.\n` +
      "- Preserve their meaning, direction, and instructional intent unless they directly conflict with accuracy or safety.\n" +
      `- Update the remaining ${artifactLabel} content so it feels coherent with those anchors.\n` +
      "- If no anchors are provided, generate normally from the context.\n"
    );
  }

  return (
    `\n\nCOHERENCE MODE: Generate as context is currently written.\n` +
    `- Use the current planning context as written to produce a fresh ${artifactLabel}.\n` +
    "- Keep the output coherent, but do not treat prior generated wording as fixed unless it is explicitly marked teacher-approved.\n"
  );
}

function buildLessonPlanVariantPrompt(variant = "primary") {
  if (variant === "challenger") {
    return (
      "\n\nHIDDEN CHALLENGER DRAFT MODE:\n" +
      "- Optimize for the best single-class-period lesson plan, not for preserving earlier AI wording.\n" +
      "- Treat unedited AI-generated blueprint or warm-up language as soft guidance only.\n" +
      "- Preserve teacher-authored constraints, but improve any repetitive, overloaded, or vague draft language.\n" +
      "- Prefer sharper scope, cleaner pacing, and a more inquiry-driven opening.\n"
    );
  }

  return (
    "\n\nPRIMARY DRAFT MODE:\n" +
    "- Build from the current planning context while preserving teacher-authored edits.\n" +
    "- Tighten scope and pacing when needed, but do not discard teacher intent.\n"
  );
}

const buildContextState = ({
  userInput,
  criteria = {},
  resourceContext = {},
  generationType,
}) => {
  const blueprint = criteria?.lessonBlueprint || {};
  const blueprintState = {
    objective: trimText(blueprint?.objective, 200),
    assessment: trimText(blueprint?.assessment, 200),
    keyPoints: toStringList(blueprint?.keyPoints || [], 6),
    lessonArc: toStringList(blueprint?.lessonArc || [], 6),
  };
  const hasBlueprint = Object.values(blueprintState).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  );

  return {
    version: CONTEXT_STATE_VERSION,
    generationType,
    goal: trimText(userInput, 240),
    criteria: {
      grades: toStringList(criteria?.grades || [], 6),
      standards: toStringList(criteria?.standards || [], 6),
      district: trimText(criteria?.district, 120),
      classPeriodLength: criteria?.classPeriodLength || null,
      udlStrategies: toStringList(criteria?.udlStrategies || [], 6),
      additional: trimText(criteria?.additional, 200),
      includeEssentials: criteria?.includeEssentials !== false,
      includeWarmUp: criteria?.includeWarmUp !== false,
      curriculumContext: criteria?.curriculumContext || null,
    },
    essentials: {
      essentialQuestions: toStringList(criteria?.essentialQuestions || [], 5),
      enduringUnderstandings: toStringList(
        criteria?.enduringUnderstandings || [],
        5,
      ),
      warmUpQuestion: trimText(criteria?.warmUpQuestion, 200),
    },
    blueprint: hasBlueprint ? blueprintState : null,
    materials: {
      driveFiles: summarizeFiles(criteria?.driveFiles || []),
      localFiles: summarizeFiles(criteria?.localFiles || []),
      oakLessons: {
        count: Array.isArray(criteria?.oakLessons)
          ? criteria.oakLessons.length
          : 0,
        titles: Array.isArray(criteria?.oakLessons)
          ? criteria.oakLessons
              .map((lesson) =>
                String(
                  lesson?.lessonTitle ||
                    lesson?.title ||
                    lesson?.lessonSlug ||
                    lesson?.slug ||
                    "",
                ).trim(),
              )
              .filter(Boolean)
              .slice(0, 3)
          : [],
      },
      resourceCount: Array.isArray(resourceContext?.relevantResources)
        ? resourceContext.relevantResources.length
        : 0,
      curriculumUnitTitles: Array.isArray(resourceContext?.curriculumUnitTitles)
        ? resourceContext.curriculumUnitTitles.slice(0, 4)
        : [],
      topCourses: Array.isArray(resourceContext?.topCourses)
        ? resourceContext.topCourses
            .map((course) => String(course?.name || "").trim())
            .filter(Boolean)
            .slice(0, 3)
        : [],
    },
    insights: summarizeInsightContext(criteria?.insightContext),
    uploadsAnalysis: summarizeUploadAnalysis(
      criteria?.uploadedMaterialsAnalysis,
    ),
    flags: {
      previousLessonPlanProvided: Boolean(criteria?.previousLessonPlanProvided),
      useAssignmentData: Boolean(criteria?.useAssignmentData),
      teacherEditedWarmUp: Boolean(criteria?.teacherEditedState?.warmUp),
      teacherEditedEssentials: Boolean(
        criteria?.teacherEditedState?.essentials,
      ),
      teacherEditedBlueprint: Boolean(criteria?.teacherEditedState?.blueprint),
    },
  };
};

const buildContextStatePrompt = (contextState) => {
  if (!contextState) return "";
  return (
    `Context State (JSON):\n${JSON.stringify(contextState, null, 2)}\n\n` +
    "Use this context to stay coherent. Treat teacher-authored or explicitly teacher-provided fields as authoritative; you may improve AI-generated draft fields if they are vague, repetitive, or overloaded. Do not repeat the JSON in the response."
  );
};

const mergeSystemPrompts = (...prompts) =>
  prompts
    .map((prompt) => (typeof prompt === "string" ? prompt.trim() : ""))
    .filter(Boolean)
    .join("\n\n");

function inferLlmSensitivity(criteria = {}, metadata = {}) {
  const explicitSensitivity = metadata?.llmSensitivity;
  if (typeof explicitSensitivity === "string" && explicitSensitivity.trim()) {
    return explicitSensitivity.trim().toLowerCase();
  }
  if (metadata?.privateVault === true) {
    return LLM_SENSITIVITY.PRIVATE_VAULT;
  }
  if (metadata?.containsStudentPii === true || criteria?.useAssignmentData) {
    return LLM_SENSITIVITY.STUDENT_PII;
  }
  return LLM_SENSITIVITY.INTERNAL;
}

function buildLlmContextTags(criteria = {}, metadata = {}) {
  const tags = new Set(
    Array.isArray(metadata?.llmContextTags) ? metadata.llmContextTags : [],
  );
  if (metadata?.privateVault === true) tags.add("private_vault");
  if (metadata?.containsStudentPii === true) tags.add("student_pii");
  if (criteria?.useAssignmentData) tags.add("gradebook");
  if (criteria?.driveFiles?.length) tags.add("drive_files");
  if (criteria?.localFiles?.length) tags.add("uploaded_files");
  return Array.from(tags).slice(0, 24);
}

function buildHiddenDraftMetadata(baseMetadata = {}, variant = "primary") {
  const existingTags = Array.isArray(baseMetadata?.llmContextTags)
    ? baseMetadata.llmContextTags
    : [];
  const nextTags = Array.from(
    new Set([...existingTags, "lesson_plan", `hidden_${variant}_draft`]),
  ).slice(0, 24);

  return {
    ...(baseMetadata || {}),
    hiddenDraftVariant: variant,
    skipHiddenChallenge: true,
    ...(variant === "challenger"
      ? {
          modelPreference:
            (typeof baseMetadata?.hiddenChallengeModelPreference === "string" &&
              baseMetadata.hiddenChallengeModelPreference.trim()) ||
            "fast",
        }
      : {}),
    llmContextTags: nextTags,
  };
}

const buildHiddenDraftSelectionMetadata = ({
  existingMetadata = {},
  decision = {},
  primaryEvaluation = null,
  challengerEvaluation = null,
  judgeSelection = null,
  challengeStrategy = null,
} = {}) => ({
  ...(existingMetadata || {}),
  hiddenDraftSelection: {
    ...decision,
    strategy: challengeStrategy?.strategy || null,
    riskScore: challengeStrategy?.riskScore ?? null,
    challengeReasons: Array.isArray(challengeStrategy?.reasons)
      ? challengeStrategy.reasons
      : [],
    challengeSignals:
      challengeStrategy && typeof challengeStrategy.signals === "object"
        ? challengeStrategy.signals
        : null,
    primaryHardFailures: primaryEvaluation?.hardFailures || [],
    challengerHardFailures: challengerEvaluation?.hardFailures || [],
    judge: judgeSelection,
  },
});

const summarizeGenerationError = (error) => {
  if (!error) return { message: "Unknown error" };
  return {
    code: error?.code || null,
    message: error?.message || String(error),
    details: error?.details || null,
  };
};

async function judgeLessonPlanCandidates({
  aiManager,
  userInput,
  criteria,
  primaryResult,
  challengerResult,
  primaryEvaluation,
  challengerEvaluation,
  preferredProvider,
  metadata,
} = {}) {
  const prompt = buildLessonPlanComparePrompt({
    userInput,
    criteria,
    primaryContent: primaryResult?.content || "",
    challengerContent: challengerResult?.content || "",
    primaryEvaluation,
    challengerEvaluation,
  });

  const judgeResult = await aiManager.generateStructuredJson({
    prompt,
    preferredProvider,
    metadata: {
      ...(metadata || {}),
      operation: "judge-lesson-plan-candidates",
      modelPreference: "quality",
      llmContextTags: Array.from(
        new Set([
          ...(Array.isArray(metadata?.llmContextTags)
            ? metadata.llmContextTags
            : []),
          "lesson_plan_judge",
        ]),
      ).slice(0, 24),
    },
    schemaHint:
      '{"winner":"A","rationale":"...","scores":{"A":{"periodRealism":5,"objectiveAssessmentCoherence":5,"blueprintAlignment":5,"warmUpFreshness":5,"resourceIntegration":5,"perspectiveAccuracy":5,"classroomUsability":5},"B":{"periodRealism":5,"objectiveAssessmentCoherence":5,"blueprintAlignment":5,"warmUpFreshness":5,"resourceIntegration":5,"perspectiveAccuracy":5,"classroomUsability":5}},"criticalNotes":["..."]}',
  });

  return judgeResult?.parsed || null;
}

async function chooseHiddenLessonPlanWinner({
  userInput,
  courses,
  criteria,
  preferredProvider,
  metadata,
  resourceContext,
  primaryResult: precomputedPrimaryResult = null,
  primaryEvaluation: precomputedPrimaryEvaluation = null,
  challengeStrategy = null,
} = {}) {
  const [primaryAttempt, challengerAttempt] = precomputedPrimaryResult
    ? [
        {
          status: "fulfilled",
          value: precomputedPrimaryResult,
        },
        await Promise.resolve(
          run(
            userInput,
            courses,
            "lessonPlan",
            criteria,
            preferredProvider,
            buildHiddenDraftMetadata(metadata, "challenger"),
            resourceContext,
          ),
        )
          .then((value) => ({ status: "fulfilled", value }))
          .catch((reason) => ({ status: "rejected", reason })),
      ]
    : await Promise.allSettled([
        run(
          userInput,
          courses,
          "lessonPlan",
          criteria,
          preferredProvider,
          buildHiddenDraftMetadata(metadata, "primary"),
          resourceContext,
        ),
        run(
          userInput,
          courses,
          "lessonPlan",
          criteria,
          preferredProvider,
          buildHiddenDraftMetadata(metadata, "challenger"),
          resourceContext,
        ),
      ]);

  if (
    primaryAttempt.status !== "fulfilled" &&
    challengerAttempt.status !== "fulfilled"
  ) {
    throw (
      primaryAttempt.reason ||
      challengerAttempt.reason ||
      new Error("Lesson plan generation failed.")
    );
  }

  if (primaryAttempt.status !== "fulfilled") {
    console.warn(
      "Hidden lesson plan primary draft failed:",
      summarizeGenerationError(primaryAttempt.reason),
    );
  }

  if (challengerAttempt.status !== "fulfilled") {
    console.warn(
      "Hidden lesson plan challenger draft failed:",
      summarizeGenerationError(challengerAttempt.reason),
    );
  }

  if (
    primaryAttempt.status !== "fulfilled" &&
    challengerAttempt.status === "fulfilled"
  ) {
    return {
      ...challengerAttempt.value,
      metadata: buildHiddenDraftSelectionMetadata({
        existingMetadata: challengerAttempt.value?.metadata,
        decision: {
          winner: "B",
          reason: "primary_generation_failed",
          usedJudge: false,
        },
        challengerEvaluation: null,
        judgeSelection: null,
        challengeStrategy,
      }),
    };
  }

  if (challengerAttempt.status !== "fulfilled") {
    return {
      ...primaryAttempt.value,
      metadata: buildHiddenDraftSelectionMetadata({
        existingMetadata: primaryAttempt.value?.metadata,
        decision: {
          winner: "A",
          reason: "challenger_generation_failed",
          usedJudge: false,
        },
        primaryEvaluation: precomputedPrimaryEvaluation,
        judgeSelection: null,
        challengeStrategy,
      }),
    };
  }

  const primaryResult = primaryAttempt.value;
  const challengerResult = challengerAttempt.value;
  const primaryEvaluation =
    precomputedPrimaryEvaluation ||
    evaluateLessonPlanCandidate({
      content: primaryResult?.content,
      criteria,
      relevantResources: resourceContext?.relevantResources || [],
    });
  const challengerEvaluation = evaluateLessonPlanCandidate({
    content: challengerResult?.content,
    criteria,
    relevantResources: resourceContext?.relevantResources || [],
  });

  const deterministicDecision = selectDeterministicLessonPlanWinner({
    primaryEvaluation,
    challengerEvaluation,
  });
  let judgeSelection = null;
  if (
    !deterministicDecision &&
    primaryEvaluation.pass &&
    challengerEvaluation.pass
  ) {
    try {
      const aiManager = getAIProviderManager();
      judgeSelection = await judgeLessonPlanCandidates({
        aiManager,
        userInput,
        criteria,
        primaryResult,
        challengerResult,
        primaryEvaluation,
        challengerEvaluation,
        preferredProvider,
        metadata,
      });
    } catch (error) {
      console.warn("Lesson plan candidate judge failed:", error);
    }
  }

  const decision =
    deterministicDecision ||
    selectLessonPlanWinner({
      primaryEvaluation,
      challengerEvaluation,
      judgeResult: judgeSelection,
    });
  const selectedResult =
    decision.winner === "B" ? challengerResult : primaryResult;

  console.log("Hidden lesson plan draft decision:", {
    strategy: challengeStrategy?.strategy || null,
    riskScore: challengeStrategy?.riskScore ?? null,
    winner: decision.winner,
    reason: decision.reason,
    primaryHardFailures: primaryEvaluation.hardFailures,
    challengerHardFailures: challengerEvaluation.hardFailures,
    judgeSelection,
  });

  return {
    ...selectedResult,
    metadata: buildHiddenDraftSelectionMetadata({
      existingMetadata: selectedResult?.metadata,
      decision,
      primaryEvaluation,
      challengerEvaluation,
      judgeSelection,
      challengeStrategy,
    }),
  };
}

export async function run(
  userInput,
  courses = [],
  generationType = "lessonPlan",
  criteria = {},
  preferredProvider = null,
  metadata = null,
  precomputedResources = null,
) {
  const resourceOptions = buildResourceOptions(criteria);
  console.log("AI Generator called with:", {
    userInput,
    courseCount: courses?.length,
    generationType,
    criteria,
    preferredProvider,
    metadata,
    resourceOptions,
    hasPrecomputedResources: Boolean(precomputedResources),
  });

  const needsResourcePrep = generationType !== "lessonBlueprint";
  let resourceContext = null;

  if (needsResourcePrep) {
    resourceContext = precomputedResources
      ? precomputedResources
      : await prepareLessonPlanResources(
          userInput,
          courses,
          generationType,
          resourceOptions,
        );
  } else {
    resourceContext = {
      keywords: extractKeywords(userInput),
      rankedCourses: [],
      highlyRelevant: [],
      somewhatRelevant: [],
      topCourses: [],
      relevantResources: [],
      extractedResourcesSection: "",
      warmupQuestionsSection: "",
      coursesSection: "",
      curriculumUnitTitles: [],
    };
  }

  const {
    topCourses = [],
    relevantResources = [],
    extractedResourcesSection = "",
    warmupQuestionsSection = "",
    coursesSection = "",
    curriculumUnitTitles = [],
  } = resourceContext || {};
  const teacherEditedState =
    criteria && typeof criteria.teacherEditedState === "object"
      ? criteria.teacherEditedState
      : {};
  const classPeriodMinutes = parseClassPeriodMinutes(
    criteria?.classPeriodLength,
  );
  const lessonPlanVariant =
    metadata?.hiddenDraftVariant === "challenger" ? "challenger" : "primary";
  const contextState = buildContextState({
    userInput,
    criteria,
    resourceContext,
    generationType,
  });
  const contextStatePrompt = buildContextStatePrompt(contextState);
  const contextStateNotice = contextStatePrompt
    ? "\n\nUse the context state provided in the system prompt to keep the output coherent."
    : "";

  if (
    generationType === "lessonPlan" &&
    metadata?.skipHiddenChallenge !== true
  ) {
    const challengeStrategy = determineLessonPlanChallengeStrategy({
      userInput,
      criteria,
      relevantResources,
      metadata,
    });

    console.log("Lesson plan challenge strategy:", challengeStrategy);

    if (challengeStrategy.strategy === "multi_draft") {
      return chooseHiddenLessonPlanWinner({
        userInput,
        courses,
        criteria,
        preferredProvider,
        metadata,
        resourceContext,
        challengeStrategy,
      });
    }

    const primaryResult = await run(
      userInput,
      courses,
      "lessonPlan",
      criteria,
      preferredProvider,
      buildHiddenDraftMetadata(metadata, "primary"),
      resourceContext,
    );
    const primaryEvaluation = evaluateLessonPlanCandidate({
      content: primaryResult?.content,
      criteria,
      relevantResources,
    });

    if (primaryEvaluation.pass) {
      return {
        ...primaryResult,
        metadata: buildHiddenDraftSelectionMetadata({
          existingMetadata: primaryResult?.metadata,
          decision: {
            winner: "A",
            reason: "primary_only_low_risk",
            usedJudge: false,
          },
          primaryEvaluation,
          challengerEvaluation: null,
          judgeSelection: null,
          challengeStrategy,
        }),
      };
    }

    return chooseHiddenLessonPlanWinner({
      userInput,
      courses,
      criteria,
      preferredProvider,
      metadata,
      resourceContext,
      primaryResult,
      primaryEvaluation,
      challengeStrategy: {
        ...challengeStrategy,
        strategy: "primary_only_recovery",
        reasons: Array.from(
          new Set([
            ...(challengeStrategy.reasons || []),
            "primary_failed_hard_checks",
          ]),
        ),
      },
    });
  }

  // Build criteria section for the prompt
  let criteriaSection = "";

  if (criteria.grades && criteria.grades.length > 0) {
    criteriaSection += `\n\nTarget Grade Levels: ${criteria.grades.join(", ")}`;
  }

  if (criteria.politicalLeaning && criteria.politicalLeaning !== "centrist") {
    criteriaSection += `\n\nPolitical Perspective: ${criteria.politicalLeaning} (This content has been self-rated and community-reviewed for political balance)`;
  }

  if (criteria.standards && criteria.standards.length > 0) {
    criteriaSection += `\n\nAlignment Standards: Please align with ${criteria.standards.join(", ")} standards where applicable`;
  }

  if (criteria.district && criteria.district.trim()) {
    criteriaSection += `\n\nSchool District: ${criteria.district} - Consider district-specific requirements if known`;
  }

  if (criteria.classPeriodLength && criteria.classPeriodLength.trim()) {
    const duration =
      criteria.classPeriodLength === "custom"
        ? "a custom duration"
        : `${criteria.classPeriodLength} minutes`;
    criteriaSection += `\n\nClass Period Length: ${duration}`;
    if (classPeriodMinutes) {
      criteriaSection += `\nThis lesson MUST fit into a single ${classPeriodMinutes}-minute class period. Prioritize one strong objective and one main assessment path over broad coverage.`;
    } else {
      criteriaSection += `\nKeep the plan realistic for a single class period and cut coverage rather than overloading the lesson.`;
    }
  }

  if (criteria.udlStrategies && criteria.udlStrategies.length > 0) {
    // Format UDL strategies into readable categories
    const udlLabels = {
      // Multiple Means of Engagement
      "udl-real-world": "Real-world connections",
      "udl-choice-autonomy": "Student choice & autonomy",
      "udl-low-stakes": "Low-stakes practice",
      "udl-community": "Community building",
      "udl-feedback": "Individualized feedback",
      "udl-group-individual": "Varied group/individual work",
      // Multiple Means of Representation
      "udl-captions-transcripts": "Captions & transcripts",
      "udl-audio-descriptions": "Audio descriptions",
      "udl-alt-text": "Alternative text for images",
      "udl-clear-vocabulary": "Clear vocabulary",
      "udl-visual-aids": "Visual aids & graphics",
      "udl-highlight-concepts": "Highlighted key concepts",
      // Multiple Means of Action/Expression
      "udl-varied-assessments": "Varied assessment methods",
      "udl-assignment-options": "Multiple assignment options",
      "udl-flexible-deadlines": "Flexible deadlines",
      "udl-communication-platforms": "Multiple communication channels",
      "udl-project-management": "Project management support",
      "udl-submission-formats": "Varied submission formats",
    };

    const formattedStrategies = criteria.udlStrategies
      .map((strategy) => udlLabels[strategy] || strategy)
      .join(", ");

    criteriaSection += `\n\nUniversal Design for Learning (UDL) Strategies: Please incorporate these accessibility features: ${formattedStrategies}`;
  }

  if (criteria.essentialQuestions && criteria.essentialQuestions.length > 0) {
    criteriaSection += `\n\nEssential Questions: ${criteria.essentialQuestions.join(" | ")}`;
  }

  if (
    criteria.enduringUnderstandings &&
    criteria.enduringUnderstandings.length > 0
  ) {
    criteriaSection += `\n\nEnduring Understandings: ${criteria.enduringUnderstandings.join(" | ")}`;
  }

  if (criteria.includeEssentials === false) {
    criteriaSection += `\n\nNote: The teacher chose not to include essential questions or enduring understandings in the final output.`;
  }

  if (criteria.warmUpQuestion && String(criteria.warmUpQuestion).trim()) {
    if (teacherEditedState.warmUp) {
      criteriaSection += `\n\nTeacher-Approved Warm-Up Question: ${criteria.warmUpQuestion.trim()}`;
      criteriaSection += `\nUse the warm-up question above in the lesson opening unless the teacher says otherwise.`;
    } else {
      criteriaSection += `\n\nCurrent Warm-Up Draft: ${criteria.warmUpQuestion.trim()}`;
      criteriaSection += `\nYou may improve the warm-up if you can make it more inquiry-driven, more specific, or less repetitive.`;
    }
  }

  if (criteria.additional && criteria.additional.trim()) {
    criteriaSection += `\n\nAdditional Requirements: ${criteria.additional}`;
  }

  if (criteria.curriculumContext) {
    const contextLine = formatCurriculumContext(criteria.curriculumContext);
    if (contextLine) {
      criteriaSection += `\n\nCurriculum Context: ${contextLine}`;
      criteriaSection += `\nUse the curriculum context above to align lesson sequencing and scope.`;
    }
  }

  const needsGradeInference = !criteria.grades || criteria.grades.length === 0;
  const needsStandardsInference =
    !criteria.standards || criteria.standards.length === 0;
  const needsInference = needsGradeInference || needsStandardsInference;
  const inferenceInstruction = needsInference
    ? `
INFERENCE TASK (do before writing the lesson plan):
- Infer the target grade level(s) and applicable standards from the topic, vocabulary, provided resources, and district context.
- If you cannot infer with confidence, output ONLY a short "CLARIFYING QUESTIONS" section with 1-2 questions and STOP (no lesson plan).
- If you can infer, include an "INFERRED CONTEXT" section listing the grade level(s) and standards you used before the lesson plan.
`
    : `
PROVIDED CONTEXT:
- Use the provided grade level(s) and standards as authoritative. Do not override them.
`;

  // Format Google Drive resources if provided
  let driveResourcesSection = "";
  if (criteria.driveFiles && criteria.driveFiles.length > 0) {
    driveResourcesSection =
      "\n\n**YOUR PERSONAL TEACHING MATERIALS FROM GOOGLE DRIVE:**\n\n";
    driveResourcesSection +=
      "The teacher has provided these materials from their Google Drive. PRIORITIZE these resources when designing the lesson:\n\n";

    criteria.driveFiles.forEach((file, index) => {
      driveResourcesSection += `${index + 1}. **${file.name}** (${file.fileType})\n`;
      driveResourcesSection += `   - Link: ${file.link}\n`;
      driveResourcesSection += `   - Last Modified: ${file.modifiedDate}\n`;
      if (file.description) {
        driveResourcesSection += `   - Description: ${file.description}\n`;
      }
      driveResourcesSection += "\n";
    });

    driveResourcesSection +=
      "**IMPORTANT**: These are the teacher's own materials. Incorporate them into the lesson plan wherever relevant. Reference them by name in your lesson activities.\n";
  }

  let localFilesSection = "";
  if (criteria.localFiles && criteria.localFiles.length > 0) {
    localFilesSection = "\n\n**UPLOADED CLASSROOM MATERIALS:**\n\n";
    criteria.localFiles.forEach((file, index) => {
      localFilesSection += `${index + 1}. **${file.name}** (${file.type || "file"})\n`;
      if (file.content) {
        localFilesSection += `   - Excerpt: ${file.content}\n`;
      }
      localFilesSection += "\n";
    });
    localFilesSection +=
      "Use these materials when relevant and cite them by name in activities or examples.\n";
  }

  let oakResourcesSection = "";
  if (criteria.oakLessons && criteria.oakLessons.length > 0) {
    const OAK_PUBLIC_LESSON_BASE =
      "https://www.thenational.academy/teachers/lessons";
    const buildOakPublicLessonUrl = (slug) =>
      slug ? `${OAK_PUBLIC_LESSON_BASE}/${encodeURIComponent(slug)}` : null;
    const resolveOakLessonUrl = (lesson) => {
      const slug = lesson.lessonSlug || lesson.slug;
      const url = lesson.lessonUrl || lesson.url;
      if (!url && slug) return buildOakPublicLessonUrl(slug);
      if (url && url.includes("open-api.thenational.academy") && slug) {
        return buildOakPublicLessonUrl(slug);
      }
      return url || null;
    };

    oakResourcesSection =
      "\n\n**OAK CURRICULUM RESOURCES (OPEN LICENSE):**\n\n";
    oakResourcesSection +=
      "Note: Oak Curriculum is UK (England)-based; verify regional alignment for non-UK standards.\n\n";
    criteria.oakLessons.forEach((lesson, index) => {
      const title =
        lesson.lessonTitle ||
        lesson.title ||
        lesson.lessonSlug ||
        lesson.slug ||
        `Oak lesson ${index + 1}`;
      const subject =
        lesson.subjectTitle || lesson.subject || lesson.subjectSlug;
      const keyStage =
        lesson.keyStageTitle || lesson.keyStage || lesson.keyStageSlug;
      const year = lesson.yearTitle || lesson.year || lesson.yearSlug;
      const unit = lesson.unitTitle || lesson.unit || lesson.unitSlug;
      const url = resolveOakLessonUrl(lesson);
      const summary =
        lesson.summary ||
        lesson.lessonSummary ||
        lesson.description ||
        lesson.notes;
      const assets = Array.isArray(lesson.assets) ? lesson.assets : [];

      oakResourcesSection += `${index + 1}. **${title}**\n`;
      if (subject || keyStage || year || unit) {
        const context = [subject, keyStage, year, unit]
          .filter(Boolean)
          .join(" | ");
        oakResourcesSection += `   - Context: ${context}\n`;
      }
      if (url) {
        oakResourcesSection += `   - Link: ${url}\n`;
      }
      if (summary) {
        oakResourcesSection += `   - Summary: ${String(summary).slice(0, 400)}\n`;
      }
      if (assets.length > 0) {
        const assetLines = assets.slice(0, 5).map((asset) => {
          const assetTitle =
            asset.title || asset.name || asset.type || "Oak asset";
          const assetUrl =
            asset.url || asset.assetUrl || asset.href || asset.downloadUrl;
          if (assetUrl && assetUrl.includes("open-api.thenational.academy")) {
            return `     - ${assetTitle}`;
          }
          return assetUrl
            ? `     - ${assetTitle}: ${assetUrl}`
            : `     - ${assetTitle}`;
        });
        oakResourcesSection += `   - Assets:\n${assetLines.join("\n")}\n`;
      }
      oakResourcesSection += "\n";
    });

    oakResourcesSection +=
      "Use these Oak lessons and assets as core resources alongside the teacher materials.\n";
  }

  let prompt;
  const choicesContext = buildUserChoicesContext(criteria, metadata);
  const nestedContext = buildNestedPromptContext(criteria);
  const insightContextSection = formatInsightContextSection(criteria);
  const uploadedMaterialAnalysisSection =
    formatUploadedMaterialAnalysisSection(criteria);
  const lessonBlueprintSection = formatLessonBlueprintSection(criteria, {
    variant: lessonPlanVariant,
  });
  const teacherAnchorSection = formatTeacherAnchorSection(criteria);
  const generationModeSection = buildGenerationModePrompt(
    criteria,
    generationType,
  );
  const lessonPlanVariantSection =
    buildLessonPlanVariantPrompt(lessonPlanVariant);

  if (generationType === "warmUp") {
    prompt = `Create a warm-up writing prompt based on the following topic and skills.

Topic and Skills: ${userInput}${contextStateNotice}${choicesContext}${nestedContext}${criteriaSection}${insightContextSection}${uploadedMaterialAnalysisSection}${warmupQuestionsSection}${driveResourcesSection}${localFilesSection}${oakResourcesSection}${extractedResourcesSection}${coursesSection}

Instructions:
- Create an engaging warm-up question that activates students' background knowledge and opens inquiry.
- The question should relate to both the topic and the thinking work students will do in the lesson.
- Make it open-ended, punchy, and classroom-ready.
- If essential questions or enduring understandings are provided, use them to sharpen the warm-up focus.
${warmupQuestionsSection ? "- IMPORTANT: Review the scraped warm-up question bank above. Adapt the strongest existing question when possible instead of writing a brand-new one." : "- If no scraped warm-up questions are available, write a fresh question that still feels specific and inquiry-driven."}
- Do NOT simply restate the lesson topic or objective.
- Format your response as just the warm-up question itself
- Do NOT include instructions about how many sentences to write
- Do NOT mention "deeper learning" or course recommendations

Warm-up question:`;
  } else if (generationType === "lessonBlueprint") {
    prompt = `Create a concise lesson blueprint (JSON only) based on the topic and planning context.

Topic and Skills: ${userInput}${contextStateNotice}${choicesContext}${nestedContext}${criteriaSection}${insightContextSection}${uploadedMaterialAnalysisSection}${teacherAnchorSection}${generationModeSection}

Instructions:
- Return JSON only in this format:
{"objective":"...","keyPoints":["..."],"assessment":"...","lessonArc":["Opening (5-10 min): ...","Instruction (15-20 min): ...","Guided Practice (15-20 min): ...","Independent Practice (10-15 min): ...","Closing (5 min): ..."]}
- Design for ONE class period and ONE main objective.
- Objective should be 1 measurable sentence.
- Key points should be 2-4 concise bullets, not a long coverage list.
- Assessment should describe one primary way students demonstrate mastery.
- Lesson arc should outline distinct, time-boxed phases whose total fits the class period.
- Each lesson arc phase must do different work. Do not repeat the same point across every phase.
- If the prompt is broad, narrow it to a realistic lesson slice rather than trying to cover everything.
- Keep it aligned to essential questions/enduring understandings if provided.

Lesson Blueprint:`;
  } else if (generationType === "lessonPlan") {
    prompt = `Create a comprehensive backward design lesson plan that integrates the specific resources provided below.

Topic and Skills: ${userInput}${contextStateNotice}${choicesContext}${nestedContext}${criteriaSection}${insightContextSection}${uploadedMaterialAnalysisSection}${lessonBlueprintSection}${teacherAnchorSection}${generationModeSection}${lessonPlanVariantSection}${warmupQuestionsSection}${driveResourcesSection}${localFilesSection}${oakResourcesSection}${extractedResourcesSection}
${inferenceInstruction}

**AI LITERACY & QUALITY STANDARDS** (CRITICAL - Apply throughout the lesson):

1. **Accuracy Filter**:
   - Cite specific historical sources, primary documents, or scholarly consensus for all factual claims
   - Flag any statements that require verification with "[Verify: source needed]"
   - Distinguish between established historical facts and interpretive claims
   - Include a brief "Historical Accuracy Notes" section highlighting key sources used

2. **Bias & Perspective Filter**:
   - In a dedicated "Voices Included" subsection, explicitly identify whose voices, experiences, and viewpoints are represented in this lesson
   - In a "Missing Voices" subsection, note whose voices or experiences are absent or underrepresented
   - Suggest at least 1-2 alternative viewpoints or counter-narratives that teachers could incorporate
   - Ensure primary sources (if used) represent diverse perspectives when possible

3. **Standards Alignment & Relevance Filter**:${
      criteria.standards && criteria.standards.length > 0
        ? `
   - Map each learning objective and major activity to SPECIFIC standard codes from: ${criteria.standards.join(", ")}
   - Include standard codes in brackets after objectives, e.g., "Students will analyze... [CCSS.ELA-LITERACY.RH.9-10.2]"
   - In the assessment section, explicitly state which standards are being assessed`
        : `
   - Ensure all activities are developmentally appropriate for the target grade level(s)
   - Connect learning objectives to recognizable academic standards where applicable`
    }${
      criteria.grades && criteria.grades.length > 0
        ? `
   - Verify that vocabulary, complexity, and cognitive demands match ${criteria.grades.join(", ")} developmental levels`
        : ""
    }

**YOUR PRIMARY TASK**: Build a realistic single-class-period lesson plan AROUND the specific resources listed above, using backward design principles (start with one objective and one assessment path, then plan instruction).

**SCOPE RULES**:
- Prioritize depth over breadth.
- Choose one core objective and one main assessment pathway.
- If tradeoffs are necessary, cut coverage rather than squeezing in more tasks.
- Make the opening use the teacher-approved warm-up question when one exists.
- If no teacher-approved warm-up exists but scraped warm-up questions are provided, adapt one of them into the opening.
- Do not let the warm-up merely restate the lesson topic.

Create a lesson plan with the following structure:

## OBJECTIVE
What should students be able to do by the end of this lesson? Write one clear, measurable objective.

## KEY POINTS
What knowledge and skills are embedded in the objective? List 2-4 key points students must understand.

## MATERIALS & RESOURCE ATTRIBUTION
List all materials needed with clear attribution:
- Specific curriculum documents from "Relevant Links" above (use exact titles and URLs)
- Specific videos from "Relevant Videos" above (use exact titles and URLs)
- Oak Curriculum lessons and assets from "Oak Curriculum Resources" above (use exact titles and URLs)
- Any additional materials needed

**Resource Attribution Map** (show intellectual honesty):
For each major resource used, indicate which lesson sections it influenced:
- [Resource Title](URL) → Used in: [Opening, Introduction, Guided Practice, etc.]
- Example: "Primary Source Collection on WWI" → Used in: Introduction (document analysis), Guided Practice (perspective comparison)

## HISTORICAL ACCURACY & SOURCE ATTRIBUTION
Briefly note the primary sources, scholarly works, or authoritative references that inform this lesson's content. Flag any claims that may require additional verification.

## VOICES ANALYSIS
**Voices Included**: Whose voices, experiences, and viewpoints are represented in this lesson?
**Missing Voices**: Whose voices or experiences are absent or underrepresented? What alternative viewpoints could be incorporated?

## ASSESSMENT
Describe what students will do to show they have mastered (or made progress toward) the objective.
- Include an exemplary student response that illustrates the expected level of rigor
- Reference specific resources from above if they're part of the assessment

## OPENING
- Time: [allocate minutes within the class period]
- How will you communicate what is about to happen and its importance?
- How will you connect to previous lessons?
- How will you engage students and capture their interest?
- Use the chosen warm-up question as a genuine inquiry launch.
- If you adapt a scraped warm-up question, keep its flavor but tailor it to this lesson.
- Use a specific resource only if it strengthens the opening without bloating the lesson.

## INTRODUCTION OF NEW MATERIAL
- Time: [allocate minutes within the class period]
- How will you explain/demonstrate all knowledge/skills required of the objective?
- Which potential misunderstandings do you anticipate and how will you address them?
- How will students interact with the material?
- How/when will you check for understanding?
- Integrate only the resources that materially strengthen this lesson slice.
- Make these resources central to instruction, not supplemental

## GUIDED PRACTICE
- Time: [allocate minutes within the class period]
- How will students practice with your support?
- How will you ensure practice is scaffolded from easy to hard without overloading the period?
- How/when will you monitor performance and address misunderstandings?
- **USE** specific documents and resources from above for practice activities
- Include direct links only when they are actively used

## INDEPENDENT PRACTICE
- Time: [allocate minutes within the class period]
- How will students independently practice to solidify their understanding?
- When and how would you intervene to support this practice?
- How will you provide opportunities for remediation and extension?
- Base this on resources provided above

## EXTENSION OPPORTUNITIES (Honors / Extra Credit)
- Provide 2-3 enrichment options for advanced learners.
- Each option must reference at least one link from the **Extension Opportunities** resources above when they are provided.
- Explain how each extension deepens or extends the core objective.

## LESSON ASSESSMENT
How will students demonstrate mastery of the objective?
- Link to specific assessment documents if available from resources above

## ASSIGNMENTS (for analytics)
Create 1-3 assignments that align to the objectives and assessment above. For each assignment, include:
- Assignment Title:
- Task:
- Evidence/Submission:
- Rubric Focus (3-5 criteria):
- Analytics Link (what this assignment reveals about understanding):
- Reference Oak lessons/assets where they strengthen the task

## CLOSING
- Time: [allocate minutes within the class period]
- How will students summarize and state the significance of what they learned?
- Reference key resources used during the lesson

**CRITICAL REQUIREMENTS**:
- The total time across lesson sections must fit within the selected class period.
- You MUST use AT LEAST 5-7 specific resource links from the "Relevant Links" and "Relevant Videos" sections above
- Each link must use the EXACT title and URL provided in markdown format: [exact title](exact-url)
- If Oak Curriculum resources are provided, incorporate at least 2 of their links into instruction or assignments
- If Extension Opportunities resources are provided, include at least 1 link in the Extension Opportunities section
- Integrate these throughout the lesson sections - NOT just listed in Materials
- Make the lesson plan BUILD ON these resources, not just mention them
- Only suggest external resources if the provided ones don't cover the topic
- **IMPORTANT**: Do NOT explain which resources you're NOT using or why certain resources don't fit
- Only mention resources that you ARE actively using in the lesson plan
- Keep the plan tightly scoped enough that a teacher could realistically teach it in one period.
- The lesson plan should be ready for teachers to use - no meta-commentary${coursesSection ? "\n\n**Additional Context** (for general reference only - prioritize the specific resources above):\n" + coursesSection : ""}

Format your response as a well-structured lesson plan with clear section headers using markdown (## for main sections).
- Do not include a top-level "Lesson Plan" heading or a standalone "Lesson Plan:" line before sections.`;
  }

  try {
    const aiManager = getAIProviderManager();

    // Enhance metadata with course information
    const modelPreference =
      metadata?.modelPreference ||
      (generationType === "lessonPlan" ? "quality" : undefined);

    const enhancedMetadata = {
      ...metadata,
      operation:
        generationType === "warmUp"
          ? "generate-warmup"
          : generationType === "lessonBlueprint"
            ? "generate-lesson-blueprint"
            : "generate-lesson-plan",
      generationType,
      criteria,
      ...(modelPreference ? { modelPreference } : {}),
      systemPrompt: mergeSystemPrompts(
        metadata?.systemPrompt,
        contextStatePrompt,
      ),
      coursesUsed: topCourses.slice(0, 5).map((c) => c.name), // Top 5 course names
      resourceCount: relevantResources?.length || 0,
      curriculumUnitTitles,
      llmSensitivity: inferLlmSensitivity(criteria, metadata || {}),
      llmContextTags: buildLlmContextTags(criteria, metadata || {}),
    };

    const result =
      generationType === "lessonBlueprint"
        ? await aiManager.generateStructuredJson({
            prompt,
            preferredProvider,
            metadata: enhancedMetadata,
            schemaHint:
              '{"objective":"...","keyPoints":["..."],"assessment":"...","lessonArc":["Opening (5-10 min): ..."]}',
          })
        : await aiManager.generate(prompt, preferredProvider, enhancedMetadata);

    console.log("AI generation result:", {
      provider: result.provider,
      providerName: result.providerName,
      usedFallback: result.usedFallback,
      contentLength: result.content?.length,
    });

    // Return both the content and metadata about which provider was used
    return {
      content: result.content,
      parsed: result.parsed || null,
      metadata: {
        provider: result.provider,
        providerName: result.providerName,
        usedFallback: result.usedFallback,
        fallbackReason: result.fallbackReason,
        usedRepair: Boolean(result.usedRepair),
        curriculumUnitTitles,
        hiddenDraftVariant: lessonPlanVariant,
      },
    };
  } catch (error) {
    console.error("Error in AI generation:", error);
    throw error;
  }
}
